import { Button, Spinner } from "@scoach/ui";
import { useEffect, useRef, useState } from "react";

import { Markdown } from "../../companies/Markdown.tsx";
import { api, streamSse } from "../../../lib/http.ts";

type Turn = { role: "user" | "model"; text: string };

/**
 * "Talk with your meeting" — ask questions answered from the meeting's transcript,
 * participants, AND generated summary (broader than the transcript-only chat). Answers
 * stream from the server (`/meetings/:id/meeting-chat/stream`, Vertex — no personal key),
 * and each Q&A is PERSISTED server-side, so this panel reloads past questions/answers.
 */
export function MeetingChat({ meetingId }: { meetingId: string }) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  // Load saved Q&A history once.
  useEffect(() => {
    let cancelled = false;
    api<{ items: { question: string; answer: string }[] }>(`/meetings/${meetingId}/meeting-chat`)
      .then((r) => {
        if (cancelled) return;
        const loaded: Turn[] = [];
        for (const it of r.items ?? []) {
          loaded.push({ role: "user", text: it.question });
          loaded.push({ role: "model", text: it.answer });
        }
        setTurns(loaded);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [meetingId]);

  async function ask() {
    const question = input.trim();
    if (!question || streaming) return;
    setError("");
    setInput("");
    const history = turns.map((t) => ({ role: t.role, text: t.text }));
    setTurns((prev) => [...prev, { role: "user", text: question }, { role: "model", text: "" }]);
    setStreaming(true);
    try {
      await streamSse(
        `/meetings/${meetingId}/meeting-chat/stream`,
        { question, history },
        {
          onDelta: (delta) => {
            setTurns((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last && last.role === "model")
                next[next.length - 1] = { ...last, text: last.text + delta };
              return next;
            });
            scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
          },
        },
      );
    } catch (e) {
      setError((e as Error).message || "Couldn't answer that. Try again.");
      setTurns((prev) => {
        const last = prev[prev.length - 1];
        return last && last.role === "model" && !last.text ? prev.slice(0, -1) : prev;
      });
    } finally {
      setStreaming(false);
    }
  }

  return (
    <section className="sum-card">
      <div className="sum-card-head">
        <h3 className="sum-h3">💬 Talk with your meeting</h3>
      </div>
      <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 8 }}>
        Ask anything about this meeting — grounded in the transcript, participants, and summary.
        Your questions &amp; answers are saved.
      </div>

      <div
        ref={scrollRef}
        style={{
          maxHeight: 320,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {turns.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--text-3)" }}>
            e.g. "What are the top risks?", "Who owns the follow-ups?", "Draft next steps based on
            what was agreed."
          </div>
        ) : (
          turns.map((t, i) =>
            t.role === "user" ? (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: append-only chat log
                key={i}
                style={{ alignSelf: "flex-end", maxWidth: "90%" }}
              >
                <div
                  style={{
                    background: "var(--gc-blue-50, #e8f0fe)",
                    color: "var(--text-1)",
                    borderRadius: 10,
                    padding: "8px 12px",
                    fontSize: 13,
                  }}
                >
                  {t.text}
                </div>
              </div>
            ) : (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: append-only chat log
                key={i}
                style={{ alignSelf: "flex-start", maxWidth: "95%" }}
              >
                {t.text ? (
                  <Markdown>{t.text}</Markdown>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      color: "var(--text-3)",
                    }}
                  >
                    <Spinner size={14} /> Thinking…
                  </div>
                )}
              </div>
            ),
          )
        )}
        {error && <div style={{ fontSize: 12, color: "var(--danger, #d93025)" }}>{error}</div>}
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <input
          type="text"
          className="setup-input"
          style={{ flex: 1 }}
          value={input}
          placeholder="Ask about this meeting…"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void ask();
            }
          }}
          disabled={streaming}
        />
        <Button onClick={() => void ask()} disabled={streaming || !input.trim()}>
          {streaming ? "…" : "Ask"}
        </Button>
      </div>
    </section>
  );
}
